<template>
    <div class="toc">
        <template v-for="chapter of filtered">
            <div v-if="chapter.chapter_type === 'unit'" class="unit" :key="chapter.chapter_id">
                <div class="unit-header flex flex-align flex-center">
                    <div class="title">Unit {{chapter.chapter_number}}: {{chapter.chapter_title}}</div>
                </div>
                <div class="children">
                    <toc :chapters="chapter.chapter_children" :search="search" :exercises="exercises" />
                </div>
            </div>
            <div v-else-if="chapter.chapter_type === 'section'" class="section collapsible" :key="chapter.chapter_id" :class="{collapsed: collapsed.includes(chapter.chapter_id)}">
                <div class="section-header flex flex-align">
                    <div class="number" @click="toggle(chapter.chapter_id)">{{chapter.chapter_number}}</div>
                    <div class="title">{{chapter.chapter_title}}</div>
                    <div class="ff"></div>
                </div>
                <div class="children">
                    <toc :chapters="chapter.chapter_children" :search="search" :exercises="exercises" />
                </div>
            </div>
            <div class="flex flex-align chapter" v-else :style="{'margin-left': (50 * (chapter.chapter_indent - 1) * (exercises ? 0 : 1)) + 'px'}" :key="chapter.chapter_id">
                <div class="number">{{chapter.chapter_number}}</div>
                <router-link class="title" :to="{name: 'chapter', params: {chapter: chapter.chapter_id}}">{{chapter.chapter_title}}</router-link>
                <div class="ff"></div>
                <a class="text-button" @click="assign(chapter)" v-if="chapter.chapter_questions">Assign</a>
                <div class="questions-icon" :class="{active: chapter.chapter_questions}" v-tooltip="'This chapter contains assessment questions.'"></div>
                <div class="simulations-icon" :class="{active: chapter.chapter_simulations}" v-tooltip="'This chapter contains simulations.'"></div>
            </div>
        </template>
    </div>
</template>

<script>
    import Assign from '@/components/modals/Assign.vue';

    export default {
        name: 'TOC',
        props: {
            chapters: Array,
            search: String,
            exercises: Boolean
        },
        computed: {
            filtered(){
                if(this.chapters){
                    return this.chapters
                        .filter(chapter => this.matches(chapter))
                        .filter(chapter => this.questions(chapter))
                        .filter(chapter => this.assigned(chapter));
                }

                return [];
            },
            product(){
                return this.$store.getters.product;
            },
            collapsed(){
                return this.$store.state.products.collapsed;
            }
        },
        methods: {
            toggle(node){
                this.$store.dispatch('products/toggle', node);
            },
            assign(chapter){
                this.$modal.show(Assign, {chapter}, {width: 700, height: 'auto', classes: 'modal'});
            },
            matches(chapter){
                if(this.search){
                    if(chapter.chapter_title.toLowerCase().includes(this.search)){
                        return true;
                    }

                    if(chapter.chapter_children){
                        return chapter.chapter_children.find(child => this.matches(child));
                    }

                    return false;
                }

                return true;
            },
            questions(chapter){
                if(this.exercises){
                    if(chapter.chapter_questions){
                        return true;
                    }

                    if(chapter.chapter_children){
                        return chapter.chapter_children.find(child => this.questions(child));
                    }

                    return false;
                }

                return true;
            },
            assigned(chapter){
                //TODO For some reason sections like to show even when they're empty if something matches the assignment title.
                //TODO Mix in the assignments here
                if(this.product.license_type === 'student'){
                    //TODO Unless this has been assigned, then replace it with the assigned version
                    if(chapter.chapter_children){
                        return chapter.chapter_children.find(child => this.assigned(child));
                    }else{
                        return chapter.chapter_questions === 0 ? chapter : null;
                    }
                }

                return true;
            },
            highlight(title){
                if(this.search){
                    return title.replace(this.search, `<span class="highlight">${this.search}</span>`);
                }

                return title;
            }
        }
    }
</script>

<style scoped lang="less">
    @import "~@/assets/less/variables";

    @unit-font-size: 18px;
    @section-font-size: 24px;
    @number-width: 50px;

    .toc
    {

    }

    .title
    {
        font-size: 15px;
        color: var(--primary-text-color);
    }

    .children
    {
        padding-left: @number-width;
        padding-top: 20px;
    }

    .number
    {
        width: @number-width;
        font-size: 15px;
        color: var(--secondary-text-color);
        position: absolute;
        left: -1 * @number-width;
        top: 0;
    }

    .collapsible > .section-header > .number
    {
        cursor: pointer;

        &::after
        {
            content: "";
            position: absolute;
            left: -24px;
            top: 6px;
            background: url('~@/assets/icons/grey/caret-down.svg') -2px -1px no-repeat;
            background-size: 21px auto;
            width: 18px;
            height: 18px;
            opacity: 0;
            transition: opacity ease 0.2s;
            z-index: 3;
        }

        &::before
        {
            content: "";
            position: absolute;
            left: -24px;
            top: 3px;
            width: 30px;
            height: 18px;
        }

        &:hover, &:active
        {
            &::after
            {
                opacity: 1;
            }
        }
    }

    .collapsible.collapsed > .section-header
    {
        > .number
        {
            &::after
            {
                opacity: 1;
                transform: rotate(-90deg);
            }
        }
    }

    .collapsed
    {
        .children
        {
            display: none;
        }
    }

    .unit
    {
        padding: 50px 0 30px;

        > .children
        {
            padding-left: 0;
            padding-top: 10px;
        }
    }

    .unit-header
    {
        .title
        {
            color: @base;
            font-size: 14px;
            font-weight: 600;
            line-height: 32px;
            padding: 0 15px;
            border-radius: 32px;
            background: fade(@base, 15%);
        }
    }

    .section
    {
        padding: 25px 0;
        // border-top: 1px solid var(--border-color);

        &:first-child
        {
            border-top: 0;
        }
    }

    .section-header
    {
        .title, .number
        {
            font-size: @section-font-size;
            font-weight: 600;
            line-height: 30px;
        }
    }

    .chapter
    {
        padding: 4px 0;
        line-height: 24px;

        .number
        {
            top: 4px;
        }

        .title
        {
            cursor: pointer;

            &::after
            {
                content: "";
                position: absolute;
                border-bottom: 1px solid var(--secondary-text-color);
                top: 0;
                left: 0;
                bottom: -2px;
                pointer-events: none;
                width: 0;
                transition: width ease 0.2s;
            }

            &:hover, &:active
            {
                &::after
                {
                    width: 100%;
                }
            }
        }

        .questions-icon, .simulations-icon
        {
            width: 24px;
            height: 24px;
            min-width: 24px;
            opacity: 0.2;
            pointer-events: none;

            &.active
            {
                opacity: 1;
                pointer-events: all;
            }
        }

        .questions-icon
        {
            background: url('~@/assets/icons/grey/questions.svg') 5px 7px no-repeat;
            background-size: 14px auto;
        }

        .simulations-icon
        {
            background: url('~@/assets/icons/grey/simulations.svg') 2px 7px no-repeat;
            background-size: 22px auto;
        }

        .text-button
        {
            margin: 0 10px;
            font-size: 15px;
            font-weight: 400;
            line-height: 24px;
            height: 24px;
        }
    }

    @media only screen and (max-width: 1100px)
    {
        .section-header
        {
            .number
            {
                position: relative;
                left: 0;
            }
        }

        .children
        {
            padding-top: 10px;
        }
    }
</style>
